import { TextField } from "@mui/material"
import {
  AssetType,
  IndustryType,
  type AssetGroup,
} from "@phc/common/gen/preferencesGen"

import type { GeocoderResult } from "@phc/common"
import type React from "react"
import { useCallback, useState } from "react"
import {
  convertGeocoderResultToLocation,
  isAdminBoundaryFromPlaceType,
} from "../../../utils/helpers/assetHelper"
import { getCentroidFromGeocoder } from "../../../utils/helpers/locationCenterHelper"
import type { Asset, AssetInput } from "../../../utils/preferencesApi"
import { toAssetInput } from "../../../utils/preferencesApi"
import { AssetGroupAutocomplete } from "./AssetGroupAutocomplete"
import { AssetTypeAutocomplete } from "./AssetTypeAutocomplete"
import { AssetGeoLocationSearchInput } from "./GeoLocationSearchInput"
import { ThreatRadiusSelect } from "./ThreatRadiusSelect"
import { useCreateAsset } from "./hooks/useCreateAsset"
import { useUpdateAsset } from "./hooks/useUpdateAsset"

import { getLocationId } from "../../../utils/helpers"
import { trackEvent } from "../../../utils/mixpanel"
import { IndustryAutocomplete } from "./IndustryAutocomplete"
import { AssetDialogControls, DialogHeader, DialogStyled } from "./Shared"

interface ManageAssetDialogProps {
  handleModalClose: () => void
  asset?: Asset
  asset_groups?: AssetGroup[]
  isLocationSelector: boolean
  addToSelectedAssets: (asset: Asset) => void
}

export const ManageAssetDialog: React.FC<ManageAssetDialogProps> = ({
  handleModalClose,
  asset,
  asset_groups,
  isLocationSelector,
  addToSelectedAssets,
}) => {
  const isUpdate = !!asset

  const [selectedGroups, setSelectedGroups] = useState<
    AssetGroup[] | undefined
  >(asset?.asset_groups ?? undefined)
  const [selectedAssetType, setSelectedAssetType] = useState(
    asset?.asset_types?.[0] ?? AssetType.ASSET_TYPE_UNSPECIFIED
  )

  const { mutateAsync: createAsset, isPending: createIsLoading } =
    useCreateAsset()
  const { mutateAsync: updateAsset, isPending: updateIsLoading } =
    useUpdateAsset()
  const [location_id, setLocationId] = useState(getLocationId(asset))
  const [name, setName] = useState(asset?.name ?? "")

  const [threat_radius, setThreatRadius] = useState<number | undefined>(
    asset?.threat_radius ?? undefined
  )
  const [selectedIndustryType, setSelectedIndustryType] = useState(
    asset?.industry ?? IndustryType.INDUSTRY_TYPE_UNSPECIFIED
  )
  const [groups, setGroups] = useState<AssetGroup[] | undefined>(asset_groups)
  const [location, setLocation] = useState<GeocoderResult | undefined>()
  const [isAdminBoundary, setIsAdminBoundary] = useState(
    isAdminBoundaryFromPlaceType(
      asset?.base_event?.geotags[0]?.mapbox_location?.place_type ?? undefined
    )
  )

  const saveAsset = useCallback(async () => {
    const existingGeotag = asset?.base_event?.geotags[0]

    const newMapboxLocation = location
      ? convertGeocoderResultToLocation(location)
      : existingGeotag?.mapbox_location

    const newGeotag = {
      location_id: location_id ?? "",
      mapbox_location: newMapboxLocation,
      wkt_centroid: getCentroidFromGeocoder(location),
    }

    const mergedGeotag = {
      ...existingGeotag,
      ...newGeotag,
    }

    const newAsset: AssetInput = {
      ...toAssetInput(asset),
      asset_id: asset?.asset_id ?? "",
      notes: asset?.notes ?? "",
      notification_settings: asset?.notification_settings ?? [],
      name,
      industry: selectedIndustryType,
      threat_radius: threat_radius ?? 0,
      asset_groups: selectedGroups ?? [],
      asset_types: [selectedAssetType],
      base_event: {
        geotags: [mergedGeotag],
        mapbox_location: asset?.base_event?.mapbox_location,
      },
    }

    if (isUpdate) {
      await updateAsset({ asset: newAsset })
    } else {
      const created = await createAsset({ asset: newAsset })

      if (isLocationSelector && created) {
        addToSelectedAssets(created)
      }
    }
    trackEvent("MANAGE_LOCATION", {
      action: isUpdate ? "edit" : "add",
      locationId: location_id,
      industry: selectedIndustryType,
    })
  }, [
    addToSelectedAssets,
    asset,
    createAsset,
    isLocationSelector,
    isUpdate,
    location,
    location_id,
    name,
    selectedAssetType,
    selectedGroups,
    selectedIndustryType,
    threat_radius,
    updateAsset,
  ])

  const mutationInProgress = createIsLoading || updateIsLoading

  return (
    <DialogStyled
      open
      onClose={handleModalClose}
      aria-labelledby={
        isUpdate ? "asset-modal-title" : "asset-modal-description"
      }
      sx={{
        ".MuiPaper-root": {
          display: "grid",
          gap: "20px",
        },
      }}
    >
      <DialogHeader
        title={isUpdate ? "Edit Location" : "Add Location"}
        desc={
          !isUpdate
            ? "Add locations to track their risk levels and stay up to date on threats concerning nearby areas."
            : undefined
        }
      />

      <AssetGeoLocationSearchInput
        onChangeGeo={setLocation}
        setLocationId={setLocationId}
        placeholder={
          asset?.base_event?.geotags[0]?.mapbox_location?.place_name ??
          undefined
        }
        setIsAdminBoundary={setIsAdminBoundary}
        setName={setName}
      />

      <TextField
        id="outlined"
        label="Location Nickname"
        value={name}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setName(event.target.value)
        }}
      />

      <AssetGroupAutocomplete
        groups={groups ?? []}
        setGroups={setGroups}
        selectedGroups={selectedGroups}
        setSelectedGroups={setSelectedGroups}
      />

      <IndustryAutocomplete
        selectedIndustry={selectedIndustryType}
        setSelectedIndustry={setSelectedIndustryType}
        isDisabled={isAdminBoundary}
      />

      <AssetTypeAutocomplete
        selectedAssetType={selectedAssetType}
        setSelectedAssetType={setSelectedAssetType}
        isDisabled={isAdminBoundary}
      />

      <ThreatRadiusSelect
        threat_radius={threat_radius}
        setThreatRadius={setThreatRadius}
        isDisabled={isAdminBoundary}
      />

      <AssetDialogControls
        handleModalClose={handleModalClose}
        isDisabled={mutationInProgress || !location}
        action={saveAsset}
        text={isUpdate ? "Save Changes" : "Add Location"}
      />
    </DialogStyled>
  )
}
